import { useQueryClient } from '@tanstack/react-query'
import { useNavigate, useRouterState } from '@tanstack/react-router'
import { Bell, CircleHelp, Keyboard, Languages, LogOut, Menu, Monitor, Moon, PlayCircle, Plus, Search, Sun, UserRound } from 'lucide-react'
import { authApi, useMe } from '@/api/auth'
import { asList, useAvailability, useCan } from '@/api/hooks'
import { useMyNotifications, NotificationsA, type StaffNotification } from '@/api/notifications'
import { Button } from '@/components/ui/button'
import { Avatar, Kbd, Tooltip } from '@/components/ui/misc'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/menu'
import { useI18n, type Lang } from '@/i18n'
import { useTheme, type ThemePref } from '@/lib/theme'
import { openCreateTask, toggleHelp, ui, useUI } from '@/lib/store'
import { HELP_BUTTON_ID } from '@/help/HelpPanel'
import { startTour } from '@/tour/state'
import { WELCOME_TOUR_ID } from '@/tour/tours'
import type { LiveState } from '@/lib/live'
import { cn } from '@/lib/utils'
import { InstallButton } from './Pwa'

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform)
export const MOD = isMac ? '⌘' : 'Ctrl'

export function Topbar({ live }: { live: LiveState }) {
  const { t } = useI18n()
  const onTasks = useRouterState({ select: (s) => s.location.pathname === '/tasks' })
  const canCreate = useCan('tasks.create') && !onTasks
  return (
    <header data-print="hide" className="sticky top-0 z-30 flex h-14 items-center gap-2 border-b border-border bg-surface/90 px-3 backdrop-blur md:px-5">
      <Button variant="ghost" size="icon" className="lg:hidden" aria-label={t('nav.openMenu')} onClick={() => ui.set({ mobileNavOpen: true })} data-tour="nav-mobile">
        <Menu />
      </Button>
      <button
        type="button"
        onClick={() => ui.set({ paletteOpen: true })}
        className="flex h-9 w-full max-w-[520px] items-center gap-2 rounded-[8px] border border-border bg-surface-2 px-3 text-left text-[13px] text-muted transition-colors hover:border-border-strong"
        aria-label={t('search.open')}
        data-testid="open-palette"
        data-tour="search"
      >
        <Search className="size-4 shrink-0" aria-hidden />
        <span className="truncate">{t('search.placeholder')}</span>
        <span className="ml-auto hidden items-center gap-1 sm:flex">
          <Kbd>{MOD}</Kbd>
          <Kbd>K</Kbd>
        </span>
      </button>
      <div className="ml-auto flex items-center gap-1">
        <Tooltip content={t(live === 'open' ? 'live.on' : live === 'connecting' ? 'live.connecting' : 'live.off')}>
          <span className="mr-1 hidden items-center gap-1.5 rounded-full px-2 py-1 text-[12px] text-muted sm:flex" role="status">
            <span className={cn('size-2 rounded-full', live === 'open' ? 'bg-success' : live === 'connecting' ? 'bg-warning' : 'bg-border-strong')} aria-hidden />
            <span className="hidden xl:inline">{t(live === 'open' ? 'live.on' : live === 'connecting' ? 'live.connecting' : 'live.off')}</span>
          </span>
        </Tooltip>
        {canCreate && (
          <Button size="sm" className="hidden sm:inline-flex" onClick={() => openCreateTask()} actionId="tasks.create" aria-keyshortcuts="c">
            <Plus /> {t('tasks.new')}
          </Button>
        )}
        <InstallButton />
        <NotificationsBell />
        <HelpButton />
        <UserMenu />
      </div>
    </header>
  )
}

function NotificationsBell() {
  const { t } = useI18n()
  const avail = useAvailability(NotificationsA.my)
  const q = useMyNotifications(avail === 'ok')
  const unread = asList<StaffNotification>(q.data).filter((n) => !n.read_at).length
  return (
    <Button
      variant="ghost"
      size="icon"
      className="relative"
      aria-label={unread ? t('notifications.titleUnread', { n: unread }) : t('notifications.title')}
      onClick={() => ui.set({ notificationsOpen: true })}
      data-tour="notifications"
    >
      <Bell />
      {unread > 0 && (
        <span className="absolute right-1 top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-brand-strong px-1 text-[10px] font-semibold text-on-brand tabular">
          {unread > 99 ? '99+' : unread}
        </span>
      )}
    </Button>
  )
}

/** «?» — opens the help panel for the current page (also ? and F1). */
function HelpButton() {
  const { t } = useI18n()
  const open = useUI((s) => s.helpOpen)
  return (
    <Tooltip content={<span className="flex items-center gap-1.5">{t('help.open')} <Kbd>?</Kbd> <Kbd>F1</Kbd></span>}>
      <Button
        id={HELP_BUTTON_ID}
        variant={open ? 'secondary' : 'ghost'}
        size="icon"
        aria-label={t('help.open')}
        aria-expanded={open}
        aria-keyshortcuts="? F1"
        onClick={() => toggleHelp()}
        data-tour="help"
        data-testid="help-button"
      >
        <CircleHelp />
      </Button>
    </Tooltip>
  )
}

export function UserMenu() {
  const { t, lang, setLang } = useI18n()
  const { theme, setTheme } = useTheme()
  const me = useMe().data
  const navigate = useNavigate()
  const qc = useQueryClient()
  const logout = async () => {
    try {
      await authApi.logout()
    } finally {
      qc.clear()
      void navigate({ to: '/login' })
    }
  }
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" className="ml-1 flex items-center gap-2 rounded-full p-0.5 pr-1 hover:bg-sunken" aria-label={t('user.menu')} data-testid="user-menu" data-tour="user-menu">
          <Avatar name={me?.name} size={30} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-60">
        <DropdownMenuLabel className="flex flex-col gap-0.5">
          <span className="text-[13px] font-semibold text-ink-strong">{me?.name}</span>
          <span className="truncate font-normal">{me?.email}</span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => void navigate({ to: '/profile' })}>
          <UserRound /> {t('user.profile')}
        </DropdownMenuItem>
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            <Languages /> {t('user.language')}
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            <DropdownMenuRadioGroup value={lang} onValueChange={(v) => setLang(v as Lang)}>
              <DropdownMenuRadioItem value="uk">Українська</DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="en">English</DropdownMenuRadioItem>
            </DropdownMenuRadioGroup>
          </DropdownMenuSubContent>
        </DropdownMenuSub>
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            {theme === 'dark' ? <Moon /> : theme === 'light' ? <Sun /> : <Monitor />} {t('user.theme')}
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            <DropdownMenuRadioGroup value={theme} onValueChange={(v) => setTheme(v as ThemePref)}>
              <DropdownMenuRadioItem value="light">{t('theme.light')}</DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="dark">{t('theme.dark')}</DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="system">{t('theme.system')}</DropdownMenuRadioItem>
            </DropdownMenuRadioGroup>
          </DropdownMenuSubContent>
        </DropdownMenuSub>
        <DropdownMenuItem onSelect={() => toggleHelp(true)}>
          <CircleHelp /> {t('user.help')}
          <Kbd className="ml-auto">?</Kbd>
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => startTour(WELCOME_TOUR_ID)} data-testid="menu-tour">
          <PlayCircle /> {t('user.tour')}
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => ui.set({ shortcutsOpen: true })}>
          <Keyboard /> {t('user.shortcuts')}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => void logout()} data-testid="logout">
          <LogOut /> {t('user.logout')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
